import { ConfirmationDialog } from 'components/ConfirmationDialog';
import type { ItemAction } from 'components/DropdownContext';
import { DropdownContext } from 'components/DropdownContext';
import type { TextFieldPropValue, TextFieldPropOnChange, TextFieldPropStatus } from 'components/FieldNumberFormat';
import { RequestStatus } from 'features/request/requestSlice';
import type { ProjectMethodologyEnum } from 'gql/graphql';
import { useAccess } from 'hooks/useAccess';
import { useDropdownContextMenuTooltipController } from 'hooks/useDropdownContextMenuTooltipController';
import { useMethodsConfirmationDialog } from 'hooks/useMethodsConfirmationDialog';
import { cnMixSpace, cnText, cnListItem, cnListItemGrid, Tooltip } from 'uikit';

import cn from 'classnames';
import { useCallback, useEffect, useMemo, useState } from 'react';

import Styles from '../../catalogCommonStyles.module.css';
import { CatalogEditValueInput } from '../../CatalogEditValueInput';
import { useRemoveVariableGroup, useUpdateVariableGroup } from '../hooks';

interface IVariableGroupsItemProps {
    id: number;
    label: string;
    count: number;
    updateGroupList: () => void;
    catalogUniqueId: string | undefined;
    isGlobal?: boolean;
    methodology: ProjectMethodologyEnum;
}

export const VariableGroupsItem = ({
    id,
    label,
    count,
    updateGroupList,
    catalogUniqueId,
    isGlobal = false,
    methodology,
}: IVariableGroupsItemProps) => {
    const { catalogs: catalogsRules } = useAccess();

    const [mode, setMode] = useState<'read' | 'edit'>('read');
    const [inputStatus, setInputStatus] = useState<TextFieldPropStatus | undefined>(undefined);
    const [groupName, setGroupName] = useState<TextFieldPropValue>(label);
    const [isSubmitted, setIsSubmitted] = useState<boolean>(false);

    const resetState = useCallback(() => {
        setMode('read');
        setInputStatus(undefined);
        setIsSubmitted(false);
    }, []);

    const { isUniqueVariableGroupData, isUniqueVariableGroupStatus, updateOtherVariableGroupHandler } =
        useUpdateVariableGroup({
            groupName: mode === 'read' ? null : groupName,
            updateGroupList,
            removeInputHandler: resetState,
            catalogUniqueId,
            isGlobal,
            methodology,
        });

    const handleRemoveGroup = useRemoveVariableGroup({ id, updateGroupList });

    useEffect(() => {
        if (
            isSubmitted &&
            isUniqueVariableGroupStatus === RequestStatus.SUCCESS &&
            isUniqueVariableGroupData === false &&
            groupName?.trim() !== label.trim()
        ) {
            setInputStatus('alert');
        }
    }, [groupName, isSubmitted, isUniqueVariableGroupData, isUniqueVariableGroupStatus, label]);

    useEffect(() => {
        if (
            isSubmitted &&
            isUniqueVariableGroupStatus === RequestStatus.SUCCESS &&
            isUniqueVariableGroupData &&
            groupName &&
            catalogUniqueId
        ) {
            updateOtherVariableGroupHandler({ id, name: groupName, catalogUniqueId });
        }
    }, [
        catalogUniqueId,
        groupName,
        id,
        isSubmitted,
        isUniqueVariableGroupData,
        isUniqueVariableGroupStatus,
        updateOtherVariableGroupHandler,
    ]);

    const handleEditGroup = useCallback(() => {
        setMode('edit');
    }, []);

    const handleCancelEditGroup = useCallback(() => {
        setGroupName(label);
        resetState();
    }, [label, resetState]);

    const handleSubmitEditGroup = useCallback(() => {
        if (groupName?.trim()) {
            setIsSubmitted(true);
        }
    }, [groupName]);

    const handleChangeGroupName = useCallback<TextFieldPropOnChange>(({ value }) => {
        setGroupName(value);
        setInputStatus(undefined);
        setIsSubmitted(false);
    }, []);

    useEffect(() => {
        if (isSubmitted && groupName?.trim() === label.trim()) {
            handleCancelEditGroup();
        }
    }, [groupName, handleCancelEditGroup, isSubmitted, label, resetState]);

    const { isOpenConfirmationDialog, handleOpenConfirmationDialog, handleCloseConfirmationDialog, handleActionQuery } =
        useMethodsConfirmationDialog({
            actionFn: handleRemoveGroup,
        });

    const dropdownItems = useMemo<ItemAction[]>(
        () => [
            { label: 'Редактировать', action: handleEditGroup },
            {
                label: 'Удалить',
                action: handleOpenConfirmationDialog,
                disabled: count > 0 || !catalogsRules?.canDelete,
                haveTooltip: count > 0 && catalogsRules?.canDelete,
                tooltipName: 'groupCount',
                tooltipMsg: 'Переменные в группе включены в формулу прочих затрат',
            },
        ],
        [catalogsRules?.canDelete, count, handleEditGroup, handleOpenConfirmationDialog],
    );

    const { getMenuItemAttributes, tooltipPosition, activeTooltipMsg, onMenuMouseMove, onMenuMouseLeave } =
        useDropdownContextMenuTooltipController();

    return mode === 'read' ? (
        <div
            className={cn(
                cnText({ lineHeight: 'xs', size: 'm', view: 'primary' }),
                cnListItem(),
                cnMixSpace({ pV: 'xs', pH: 's' }),
                cnListItemGrid(),
            )}
        >
            <div className={Styles.content}>
                <span>
                    {label}
                    <span className={cn(Styles.count, cnMixSpace({ mL: 'xs' }))}>{count}</span>
                </span>
                <DropdownContext
                    getItemAttributesHandler={getMenuItemAttributes}
                    onMouseMoveHandler={onMenuMouseMove}
                    onMouseLeaveHandler={onMenuMouseLeave}
                    items={dropdownItems}
                    direction="downStartRight"
                    size="m"
                    form="brick"
                />
                {activeTooltipMsg && (
                    <Tooltip
                        size="m"
                        direction="upCenter"
                        position={tooltipPosition}
                        isInteractive={false}
                        style={{ zIndex: 2 }}
                    >
                        {activeTooltipMsg}
                    </Tooltip>
                )}
                <ConfirmationDialog
                    textHeader="Удаление группы переменных"
                    textConfirmation="Вы уверены, что хотите удалить группу переменных?"
                    cancelButtonText="Не удалять"
                    confirmButtonText="Удалить"
                    isOpened={isOpenConfirmationDialog}
                    onSubmit={handleActionQuery}
                    onClose={handleCloseConfirmationDialog}
                />
            </div>
        </div>
    ) : (
        <CatalogEditValueInput
            value={groupName}
            onChange={handleChangeGroupName}
            onCancel={handleCancelEditGroup}
            onSubmit={handleSubmitEditGroup}
            status={inputStatus}
            tooltipMsg="Группа с таким наименованием уже существует"
            inputPlaceholder="Введите наименование группы"
        />
    );
};

VariableGroupsItem.displayName = 'VariableGroupsItem';
